import React, { ElementType, forwardRef } from 'react';
import { cx } from '@linaria/atomic';
import { Icon, IconName } from '@frontend/icons';
import { CommonHTMLAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { createKeydownHandler } from '../../../helpers';
import { IconProps } from '../../../icon';
import { NakedButton } from '../../../naked';
import { PolymorphicComponentPropWithRef, PolymorphicRef } from '../../../type-utils';
import { Chip } from '../../chip';
import { IconButton } from '../../icon-button';
import { Text } from '../../text';
import { useTooltip } from '../../tooltip';
import { Truncated } from '../../truncated';
import * as styles from '../accordion.styles';
import { useAccordionContext } from '../provider/accordion.provider';
import { useAccordionItemContext } from './accordion-item';
import { DefaultAccordionHeader } from './default-accordion-header';

type Props = {
  as?: ElementType;
  title?: string;
  subtitle?: string;
  disabled?: boolean;
  chevronIcon?: React.FC<React.PropsWithChildren<IconProps>> | boolean;
  children?: React.ReactNode;
};

type AccordionHeaderProps<C extends React.ElementType> = PolymorphicComponentPropWithRef<C, Props>;

const locationPadding = {
  small: [1, 2],
  large: [1.5, 2],
};

const DEFAULT_BOTTOM_BORDER_VARIANTS = ['default', 'error', 'filled', 'blank', 'bordered'];

type AccordionHeaderComponent = <C extends React.ElementType = 'section'>(
  props: AccordionHeaderProps<C>
) => React.ReactNode;

// eslint-disable-next-line react/display-name
export const AccordionHeaderOptional: AccordionHeaderComponent = forwardRef(
  <C extends React.ElementType = 'section'>(
    {
      disabled,
      onKeyDown,
      onClick,
      chevronIcon,
      children,
      title,
      subtitle,
      as,
      className,
      ...others
    }: AccordionHeaderProps<C>,
    ref: PolymorphicRef<C>
  ) => {
    const {
      isItemActive,
      headingLevel,
      onChange,
      getHeaderId,
      getBodyId,
      shouldLoop,
      isInteractive,
      variant = 'default',
      chevronPosition = 'right',
      chevronIcon: globalChevron,
      chevronSize,
      padding: headerPadding,
      hasMultiLocations: hasMultiLocationsGlobal,
      size,
      showBorderBottomAlways,
      startAdornment,
    } = useAccordionContext();
    const { value, hasMultiLocations, trackingId } = useAccordionItemContext();
    const isActive = isItemActive(value);
    const isLocationMulti = hasMultiLocations ?? hasMultiLocationsGlobal;

    const applyBottomBorder =
      variant === 'hybrid'
        ? isActive && !!showBorderBottomAlways
        : DEFAULT_BOTTOM_BORDER_VARIANTS.includes(variant) && (isActive || !!showBorderBottomAlways);

    const headerClass = cx(
      styles.headerBaseStyle,
      styles.headerChevronPositionStyles[chevronPosition],
      styles.headerVariantStyles[variant],
      isActive ? styles.headerActiveStyles[variant] : styles.headerInactiveStyles[variant],
      isActive && variant === 'location' && isLocationMulti && styles.headerActiveMultiLocationStyle,
      applyBottomBorder && styles.headerBorderBottomStyle,
      variant === 'error' && styles.headerErrorSpanStyle,
      disabled && styles.headerDisabledStyle,
      !isInteractive && styles.headerFocusRingStyle,
      className
    );

    const chevronContainerClass = cx(styles.chevronContainerStyle, disabled && styles.chevronContainerDisabledStyle);
    const chevronMarginClass = styles.chevronMarginStyles[chevronPosition === 'left' ? 'left' : 'right'];
    const chevronAnimatedClass = cx(styles.chevronAnimatedStyle, isActive && styles.chevronAnimatedOpenStyle);

    const paddingValue =
      typeof headerPadding !== 'undefined'
        ? theme.spacing(...(typeof headerPadding === 'number' ? [headerPadding] : headerPadding))
        : theme.spacing(...(variant === 'location' && size ? locationPadding[size] : [2]));

    const conditionalProps = {
      disabled: disabled,
      onClick: (event) => {
        onClick?.(event);
        onChange?.(value);
      },
      'data-active': isActive || undefined,
      'aria-expanded': isActive,
      'aria-controls': getBodyId(value),
      id: getHeaderId(value),
      'data-trackingid': trackingId,
    };

    const handleConditionalChevronRender = () => {
      const ChevronIcon = chevronIcon || globalChevron;

      if (!ChevronIcon || typeof ChevronIcon === 'boolean') return null;

      if (isInteractive) {
        return (
          <IconButton label='chevron' className={chevronMarginClass} {...conditionalProps}>
            <div className={chevronAnimatedClass}>
              <ChevronIcon size={chevronSize ?? variant === 'location' ? 16 : 24} />
            </div>
          </IconButton>
        );
      }

      return (
        <section className={cx(chevronContainerClass, chevronMarginClass)}>
          <div className={chevronAnimatedClass}>
            <ChevronIcon size={chevronSize ?? variant === 'location' ? 16 : 24} />
          </div>
        </section>
      );
    };

    const MainHeading = headingLevel ? `h${headingLevel}` : ('h3' as React.ElementType);
    const Component = (as as 'article') ?? isInteractive ? 'article' : NakedButton;

    return (
      <MainHeading className={styles.headingWrapperStyle}>
        <Component
          {...others}
          {...(!isInteractive && conditionalProps)}
          ref={ref}
          data-accordion-control
          onKeyDown={createKeydownHandler({
            siblingSelector: '[data-accordion-control]',
            parentSelector: '[data-accordion]',
            shouldActivateOnFocus: false,
            shouldLoop,
            orientation: 'vertical',
            onKeyDown,
          })}
          className={headerClass}
        >
          {handleConditionalChevronRender()}
          <div className={styles.headerContentContainerStyle} style={{ padding: paddingValue }}>
            {startAdornment}
            <DefaultAccordionHeader title={title} subtitle={subtitle} />
            {children}
          </div>
        </Component>
      </MainHeading>
    );
  }
);

type AccordionIconHeaderProps = {
  title: string;
  icon: IconName;
  infoTooltip?: string;
  timezone?: string;
} & CommonHTMLAttributes;

const AccordionIconHeader = ({ title, icon, infoTooltip, timezone, ...rest }: AccordionIconHeaderProps) => {
  const { isItemActive } = useAccordionContext();
  const { value } = useAccordionItemContext();
  const isActive = isItemActive(value);
  const { Tooltip, tooltipProps, triggerProps } = useTooltip();

  return (
    <span className={styles.iconHeaderStyle} {...rest}>
      <Icon name={icon} size={16} className={styles.flexShrinkNoneStyle} />
      <Truncated as={Text} size='large' className={styles.iconHeaderTitleStyle}>
        {title}
      </Truncated>

      {!!infoTooltip && (
        <span {...triggerProps} className={styles.flexShrinkNoneStyle}>
          <Icon name='info-small' size={16} className={styles.inheritColorStyle} />
        </span>
      )}
      {!!timezone && <Chip.TimeZone variant={isActive ? 'neutralDark' : 'neutral'}>{timezone}</Chip.TimeZone>}
      <Tooltip {...tooltipProps}>{infoTooltip}</Tooltip>
    </span>
  );
};

const AccordionLocationHeader = (props: Omit<AccordionIconHeaderProps, 'icon'>) => {
  const { hasMultiLocations: hasMultiLocationsGlobal } = useAccordionContext();
  const { hasMultiLocations } = useAccordionItemContext();

  const icon: IconName = hasMultiLocations ?? hasMultiLocationsGlobal ? 'locations-small' : 'location-small';

  return <AccordionIconHeader {...props} icon={icon} />;
};

const AccordionHeaderNamespace = Object.assign(AccordionHeaderOptional, {
  Location: AccordionLocationHeader,
  Icon: AccordionIconHeader,
});

export { AccordionHeaderNamespace as AccordionHeader };
